import React, { useState, useEffect, useCallback, memo } from "react";
import { Search as SearchIcon, X, Filter } from "lucide-react";

const Search = memo(
  ({
    placeholder = "Search in results...",
    onSearch,
    initialValue = "",
    showFilters = false,
    filterOptions = [],
    activeFilter = null,
    onFilterChange = () => {},
  }) => {
    const [inputValue, setInputValue] = useState(initialValue);
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);

    // When initialValue prop changes, update local state
    useEffect(() => {
      setInputValue(initialValue);
    }, [initialValue]);

    // Debounce search to avoid excessive calls
    useEffect(() => {
      const debounceTimer = setTimeout(() => {
        if (onSearch) {
          onSearch(inputValue);
        }
      }, 300);

      return () => clearTimeout(debounceTimer);
    }, [inputValue, onSearch]);

    // Handle input change
    const handleChange = useCallback((e) => {
      setInputValue(e.target.value);
    }, []);

    // Clear search
    const handleClear = useCallback(() => {
      setInputValue("");
      if (onSearch) {
        onSearch("");
      }
    }, [onSearch]);

    // Handle filter selection
    const handleFilterSelect = useCallback(
      (filter) => {
        onFilterChange(filter);
        setShowFilterDropdown(false);
      },
      [onFilterChange]
    );

    return (
      <div className="relative w-full">
        <div className="relative flex items-center">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />

          <input
            type="text"
            placeholder={placeholder}
            value={inputValue}
            onChange={handleChange}
            className="w-full pl-10 pr-16 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-green-300 focus:border-green-300 text-gray-700"
          />

          {inputValue && (
            <button
              onClick={handleClear}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}

          {showFilters && (
            <button
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              className={`absolute right-10 top-1/2 transform -translate-y-1/2 p-1 rounded-full ${
                activeFilter ? "text-green-600 bg-green-50" : "text-gray-400 hover:text-gray-600"
              }`}
              aria-label="Show filters"
            >
              <Filter size={16} />
            </button>
          )}
        </div>

        {/* Filter dropdown */}
        {showFilters && showFilterDropdown && (
          <div className="absolute z-10 mt-1 w-full bg-white rounded-lg border border-gray-200 shadow-lg">
            <div className="p-2 max-h-60 overflow-y-auto">
              {filterOptions.map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => handleFilterSelect(filter.value)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm ${
                    activeFilter === filter.value ? "bg-green-50 text-green-800" : "hover:bg-gray-50 text-gray-700"
                  }`}
                >
                  {filter.label}
                </button>
              ))}
              {activeFilter && (
                <button
                  onClick={() => handleFilterSelect(null)}
                  className="w-full text-left px-3 py-2 rounded-md text-sm text-gray-500 hover:bg-gray-50"
                >
                  Clear filter
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default Search;
